import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BarChart } from './_finHelpers';

// Present Value Calculator — two sections: Future Money + Periodical Deposits
export default function PresentValueCalc() {
  return (
    <CalcShell look="cnet" title="Present Value Calculator" subtitle="Compute the present value of a future lump sum or a stream of periodic deposits."
      about={<><p>The <b>present value</b> (PV) is the current worth of a future sum, discounted at a given rate. It answers: "How much do I need today to reach a future goal?"</p></>}
    >
      <PVLumpSum/>
      <hr className="my-8 border-slate-200"/>
      <PVPeriodic/>
    </CalcShell>
  );
}
function PVLumpSum() {
  const [FV, setFV] = useState(1000);
  const [N, setN] = useState(10);
  const [IY, setIY] = useState(6);
  const c = useMemo(() => {
    const pv = FV / Math.pow(1 + IY/100, N);
    return { pv, interest: FV - pv };
  }, [FV, N, IY]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Present Value of Future Money</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Future Value (FV)</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={FV} onChange={e=>setFV(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Number of Periods (N)</label><input type="number" value={N} onChange={e=>setN(+e.target.value||0)}/></div>
          <div className="field"><label>Interest Rate (I/Y)</label><div className="flex gap-2"><input type="number" step="0.01" value={IY} onChange={e=>setIY(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Present Value</div><div className="result-value" data-testid="pv-lump">{fmt(c.pv)}</div></div>
          <table className="data-table"><tbody><tr><td>Total Interest</td><td className="text-right text-orange-600">{fmt(c.interest)}</td></tr></tbody></table>
        </div>
      </div>
    </div>
  );
}
function PVPeriodic() {
  const [N, setN] = useState(10);
  const [IY, setIY] = useState(6);
  const [PMT, setPMT] = useState(100);
  const [when, setWhen] = useState('end');
  const c = useMemo(() => {
    const r = IY/100;
    const b = when === 'beginning' ? 1 : 0;
    const pv = r === 0 ? PMT * N : PMT * (1 - Math.pow(1+r, -N))/r * (1 + r*b);
    const fv = pv * Math.pow(1 + r, N);
    const principal = PMT * N;
    return { pv, fv, principal, interest: fv - principal };
  }, [N, IY, PMT, when]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Present Value of Periodical Deposits</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Number of Periods (N)</label><input type="number" value={N} onChange={e=>setN(+e.target.value||0)}/></div>
          <div className="field"><label>Interest Rate (I/Y)</label><div className="flex gap-2"><input type="number" step="0.01" value={IY} onChange={e=>setIY(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Periodic Deposit (PMT)</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={PMT} onChange={e=>setPMT(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">/ period</span></div></div>
          <div className="field"><label>PMT made at the</label>
            <div className="flex gap-4 text-sm mt-1">
              <label className="flex items-center gap-1"><input type="radio" checked={when==='beginning'} onChange={()=>setWhen('beginning')}/> beginning</label>
              <label className="flex items-center gap-1"><input type="radio" checked={when==='end'} onChange={()=>setWhen('end')}/> end</label>
              <span className="text-slate-500">of each compound period</span>
            </div>
          </div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Present Value</div><div className="result-value" data-testid="pv-periodic">{fmt(c.pv)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>FV (Future Value)</td><td className="text-right">{fmt(c.fv)}</td></tr>
            <tr><td>Total Principal</td><td className="text-right">{fmt(c.principal)}</td></tr>
            <tr><td>Total Interest</td><td className="text-right text-orange-600">{fmt(c.interest)}</td></tr>
          </tbody></table>
          <div className="mt-3"><PieChart parts={[{label:'Principal',value:c.principal,color:'#2563eb'},{label:'Interest',value:c.interest,color:'#10b981'}]} size={160}/></div>
        </div>
      </div>
      {/* Balance schedule bar graph */}
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Balance Accumulation Graph</h3>
        <BarChart
          data={Array.from({length: N}, (_, i) => {
            const period = i + 1;
            const r = IY/100;
            const b = when === 'beginning' ? 1 : 0;
            const fvAcc = r === 0 ? PMT * period : PMT * ((Math.pow(1+r, period) - 1)/r) * (1 + r*b);
            const principalAcc = PMT * period;
            return {
              label: String(period),
              values: [
                { value: principalAcc, color: '#2563eb' },
                { value: Math.max(0, fvAcc - principalAcc), color: '#f97316' },
              ]
            };
          })}
          stack={true}
          legend={[
            { label: 'Total Principal', color: '#2563eb' },
            { label: 'Interest', color: '#f97316' },
          ]}
        />
      </div>
    </div>
  );
}
